export type ActiveJobStatus =
  | 'going-to-pickup'
  | 'at-pickup'
  | 'going-to-delivery'
  | 'at-delivery';

const STEP_RANK: Record<ActiveJobStatus, number> = {
  'going-to-pickup': 0,
  'at-pickup': 1,
  'going-to-delivery': 2,
  'at-delivery': 3,
};

/** Prefer the furthest step reached — keeps local at-pickup if Firestore sync lagged */
export function resolveJobStep(
  serverStep: ActiveJobStatus,
  sessionStep: ActiveJobStatus | null
): ActiveJobStatus {
  if (!sessionStep) return serverStep;
  return STEP_RANK[sessionStep] > STEP_RANK[serverStep] ? sessionStep : serverStep;
}

const JOB_ID_KEY = 'go7-active-job-id';
const JOB_STATUS_KEY = 'go7-active-job-status';
const AUTO_NAV_KEY = 'go7-auto-start-nav';
const PICKUP_DONE_PREFIX = 'go7-pickup-done-';

/** Clear delivery-related session keys (e.g. after deploy or logout) */
export function clearDeliverySessionStorage(): void {
  const keysToRemove: string[] = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (!key) continue;
    if (
      key === JOB_ID_KEY ||
      key === JOB_STATUS_KEY ||
      key === AUTO_NAV_KEY ||
      key.startsWith(PICKUP_DONE_PREFIX)
    ) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((key) => sessionStorage.removeItem(key));
}

export function markPickupConfirmed(jobId: string): void {
  sessionStorage.setItem(`${PICKUP_DONE_PREFIX}${jobId}`, '1');
}

export function isPickupConfirmed(jobId: string): boolean {
  return sessionStorage.getItem(`${PICKUP_DONE_PREFIX}${jobId}`) === '1';
}

export function clearPickupConfirmed(jobId: string): void {
  sessionStorage.removeItem(`${PICKUP_DONE_PREFIX}${jobId}`);
}

export function persistActiveDelivery(jobId: string, status: ActiveJobStatus): void {
  sessionStorage.setItem(JOB_ID_KEY, jobId);
  sessionStorage.setItem(JOB_STATUS_KEY, status);
}

export function clearActiveDelivery(jobId?: string): void {
  const id = jobId || sessionStorage.getItem(JOB_ID_KEY);
  if (id) clearPickupConfirmed(id);
  clearDeliverySessionStorage();
}

export function getPersistedActiveJobId(): string | null {
  return sessionStorage.getItem(JOB_ID_KEY);
}

export function getPersistedJobStatus(): ActiveJobStatus | null {
  const status = sessionStorage.getItem(JOB_STATUS_KEY);
  if (
    status === 'going-to-pickup' ||
    status === 'at-pickup' ||
    status === 'going-to-delivery' ||
    status === 'at-delivery'
  ) {
    return status;
  }
  return null;
}

export function setAutoStartNavigation(enabled: boolean): void {
  if (enabled) {
    sessionStorage.setItem(AUTO_NAV_KEY, '1');
  } else {
    sessionStorage.removeItem(AUTO_NAV_KEY);
  }
}

export function consumeAutoStartNavigation(): boolean {
  const shouldStart = sessionStorage.getItem(AUTO_NAV_KEY) === '1';
  if (shouldStart) sessionStorage.removeItem(AUTO_NAV_KEY);
  return shouldStart;
}

/** Map Firestore order status to in-app delivery step */
export function jobStatusFromOrderStatus(rawStatus: string): ActiveJobStatus {
  const status = rawStatus.toLowerCase().replace(/\s+/g, '_');

  if (['at_pickup', 'at-pickup', 'arrived_at_pickup'].includes(status)) {
    return 'at-pickup';
  }
  if (['picked_up', 'picked-up', 'pickup_complete'].includes(status)) {
    return 'going-to-delivery';
  }
  if (
    [
      'in_transit',
      'in-transit',
      'in_progress',
      'in-progress',
      'enroute_dropoff',
      'en_route_dropoff',
      'enroute',
      'on_the_way',
      'on-the-way',
      'delivering',
    ].includes(status)
  ) {
    return 'going-to-delivery';
  }
  if (['nearby', 'at_delivery', 'at-delivery', 'arrived_at_delivery'].includes(status)) {
    return 'at-delivery';
  }
  if (
    [
      'assigned',
      'accepted',
      'enroute_pickup',
      'en_route_pickup',
      'heading_to_pickup',
      'going_to_pickup',
    ].includes(status)
  ) {
    return 'going-to-pickup';
  }
  return 'going-to-pickup';
}

export function screenForJobStep(step: ActiveJobStatus): 'navigation' | 'pickup-confirmation' | 'delivery-confirmation' {
  // Stay on the map for pickup — photos only after rider taps "Arrived at Pickup"
  if (step === 'at-delivery') return 'delivery-confirmation';
  return 'navigation';
}
